import { useEffect, useRef, useState } from 'react'

import { isEqual, noop } from 'lodash-es'

import {
  convertTriageTreatmentOptionToProgram,
  FavoritedProvidersMetadata,
  FavoritedProvidersValidationAction,
  getMostRecentHighRiskSearchDateFromUserSearchHistory,
  isChildSearch,
  TriageSearch,
} from '@lyrahealth-inc/shared-app-logic'

/** Get the list of providers who were favorited before the most recent high risk search was conducted */
const getListProviderIdsToUnfavorite = ({
  favoritedProvidersByIds,
  favoritedProvidersMetadataMap,
  highRiskSearchDate,
}: {
  favoritedProvidersByIds: string[]
  highRiskSearchDate: string | null
  favoritedProvidersMetadataMap: Record<string, FavoritedProvidersMetadata>
}) => {
  if (!highRiskSearchDate) return []

  // Search dates are stored in UTC string timestamp -- convert to unix MS timestamp for easy comparison
  const highRiskSearchDateUnixTimestamp = new Date(highRiskSearchDate + 'Z').getTime()
  // Return all providers who were favorited before the high risk search was conducted. These providers will be unfavorited
  return favoritedProvidersByIds.filter((providerId: string) => {
    const providerMetadata = favoritedProvidersMetadataMap[providerId]
    return providerMetadata.createDateUnixTimestamp < highRiskSearchDateUnixTimestamp
  })
}

// Check if any favorited providers are no longer compatible with the user's current programs,
// based on the treatmentPreference stored in favoritedProvidersMetadataMap
const checkProvidersForProgramIncompatibility = ({
  favoritedProvidersMetadataMap,
  customerSupportedPrograms,
}: {
  favoritedProvidersMetadataMap: Record<string, FavoritedProvidersMetadata> | null
  customerSupportedPrograms: string[]
}) => {
  if (!favoritedProvidersMetadataMap) return false
  const programIncompatible = Object.values(favoritedProvidersMetadataMap).some(
    (providerMetadata: FavoritedProvidersMetadata) => {
      const { treatmentPreference } = providerMetadata
      if (!!treatmentPreference) {
        const providerProgram = convertTriageTreatmentOptionToProgram(treatmentPreference, true)

        if (!providerProgram) return true
        return !customerSupportedPrograms.includes(providerProgram.customerPropertyId)
      }
      return false
    },
  )
  return programIncompatible
}

/**
 * Hook to compute any checks we need for verifying that all currently favorited providers are valid for the user
 * current use cases include:
 * if the user favorited any providers, then subsequently indicated SI/high risk in a new search
 * if the users program / benefits changed (i.e. lost access to coaching)
 */
export const useValidateFavoritedProvidersForUser = ({
  userId,
  searches,
  favoritedProvidersByIds,
  favoritedProvidersMetadataMap,
  customerSupportedPrograms,
  clearFavoritedProvidersForUser,
  isScreenFocused,
  prevIsScreenFocused,
  setIsLoading = noop,
}: {
  userId: string
  searches: TriageSearch[]
  favoritedProvidersByIds: string[] | null
  favoritedProvidersMetadataMap: Record<string, FavoritedProvidersMetadata> | null
  customerSupportedPrograms: string[]
  clearFavoritedProvidersForUser: ({
    userId,
    providerIds,
    isClearAll,
  }: {
    userId: string
    providerIds: string[]
    isClearAll?: boolean
  }) => Promise<any>
  isScreenFocused?: boolean
  prevIsScreenFocused?: boolean
  setIsLoading?: (isLoading: boolean) => void
}) => {
  const lastValidatedFavoritedProvidersByIds = useRef<string[] | null>(null)
  const lastValidatedSearches = useRef<TriageSearch[] | null>(null)
  const [validationComplete, setValidationComplete] = useState<boolean | null>(null)
  const [validationAction, setValidationAction] = useState<FavoritedProvidersValidationAction | null>(null)

  useEffect(() => {
    // Mobile only since screens are active in the background -- reset validation when refocusing and provider list has changed
    if (isScreenFocused && prevIsScreenFocused === false) {
      // If searches or provider list has changed, reset validation
      if (
        !isEqual(lastValidatedFavoritedProvidersByIds.current, favoritedProvidersByIds) ||
        !isEqual(lastValidatedSearches.current, searches)
      ) {
        setIsLoading(true)
        setValidationComplete(null)
        setValidationAction(null)
      }
    }
  }, [isScreenFocused, prevIsScreenFocused, favoritedProvidersByIds, searches, setIsLoading])

  useEffect(() => {
    if (
      isScreenFocused === false || // Skip if explicitly not focused (mobile)
      !searches ||
      !favoritedProvidersByIds ||
      !favoritedProvidersMetadataMap ||
      validationComplete !== null
    ) {
      return
    }

    const validateProviders = async () => {
      const programIncompatible = checkProvidersForProgramIncompatibility({
        favoritedProvidersMetadataMap,
        customerSupportedPrograms,
      })

      // Handle program incompatibility by clearing all saved favorited providers
      if (programIncompatible) {
        setValidationAction(FavoritedProvidersValidationAction.ALL_PROVIDERS_CLEARED)
        await clearFavoritedProvidersForUser({
          userId,
          providerIds: favoritedProvidersByIds,
          isClearAll: true,
        })
        // program incompatibility found
        lastValidatedFavoritedProvidersByIds.current = favoritedProvidersByIds
        lastValidatedSearches.current = searches
        setValidationComplete(true)
        return
      }
      const userSearches = searches.filter((search: TriageSearch) => !isChildSearch(search))
      const highRiskSearchDate = getMostRecentHighRiskSearchDateFromUserSearchHistory({ searches: userSearches })

      // Handle high risk search - clear providers favorited before the high risk search
      if (highRiskSearchDate) {
        const providerIdsToUnfavorite = getListProviderIdsToUnfavorite({
          highRiskSearchDate,
          favoritedProvidersByIds,
          favoritedProvidersMetadataMap,
        })

        if (providerIdsToUnfavorite.length === 0) {
          // No providers to unfavorite
          setValidationAction(FavoritedProvidersValidationAction.NONE)
        } else if (providerIdsToUnfavorite.length === favoritedProvidersByIds.length) {
          // All providers need to be unfavorited
          setValidationAction(FavoritedProvidersValidationAction.ALL_PROVIDERS_CLEARED)
          await clearFavoritedProvidersForUser({
            userId,
            providerIds: providerIdsToUnfavorite,
            isClearAll: true,
          })
        } else {
          // Some providers need to be unfavorited
          setValidationAction(FavoritedProvidersValidationAction.REFINED_FAVORITES)
          await clearFavoritedProvidersForUser({
            userId,
            providerIds: providerIdsToUnfavorite,
          })
        }
        // high risk search found
        lastValidatedFavoritedProvidersByIds.current = favoritedProvidersByIds
        lastValidatedSearches.current = searches
        setValidationComplete(true)
        return
      }

      // No high risk search or program incompatibility found - no providers to unfavorite
      lastValidatedFavoritedProvidersByIds.current = favoritedProvidersByIds
      lastValidatedSearches.current = searches
      setValidationComplete(true)
      setValidationAction(FavoritedProvidersValidationAction.NONE)
    }

    setValidationComplete(false)
    validateProviders()
  }, [
    userId,
    customerSupportedPrograms,
    searches,
    favoritedProvidersByIds,
    favoritedProvidersMetadataMap,
    validationComplete,
    clearFavoritedProvidersForUser,
    isScreenFocused,
  ])

  return {
    validationComplete,
    validationAction,
  }
}
